import { ApolloError } from '@apollo/client';
import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { Formik, FormikHelpers, useField } from 'formik';
import {
  CompanyPageCompanyFragment,
  useCompanyPageUpdateCompanyMutation,
} from 'generated/graphql';
import { useState } from 'react';
import {
  enableDisableRadioButtonOptions,
  EnableDisableValue,
} from 'views/Company/CompanyView/CompanyView.helpers';
import {
  ALTERNATIVE_SUPPORT_EMAIL_ADDRESS_LABEL,
  ALTERNATIVE_SUPPORT_EMAIL_DISABLING_DISCLAIMER,
  ALTERNATIVE_SUPPORT_EMAIL_ENABLING_DISCLAIMER,
  ALTERNATIVE_SUPPORT_EMAIL_LABEL,
  EXPLICIT_CONSENT_EMAIL_HINT,
  EXPLICIT_CONSENT_EMAIL_LABEL,
  EXPLICIT_CONSENT_EMAIL_NOT_SET,
  LEGAL_ACKNOWLEDGEMENTS_DISABLING_DISCLAIMER,
  LEGAL_ACKNOWLEDGEMENTS_ENABLING_DISCLAIMER,
  LIVE_CHAT_DISABLING_DISCLAIMER,
  LIVE_CHAT_ENABLING_DISCLAIMER,
  OtherSectionFormValues,
  otherSectionValidationSchema,
} from 'views/Company/CompanyView/components/OtherSection/OtherSection.helpers';
import {
  Button,
  EditFormWrapper,
  FormikRadioButtonGroup,
  FormikTextInputField,
  InlineNotification,
  NotificationType,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import styles from './OtherSection.module.scss';

type OtherSectionProps = {
  company: CompanyPageCompanyFragment;
};

function OtherSection({ company }: OtherSectionProps) {
  const [isEditing, setIsEditing] = useState(false);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
  };

  const handleSave = () => {
    setIsEditing(false);
  };

  return isEditing ? (
    <OtherSectionForm
      company={company}
      onCancelClick={handleCancelClick}
      onSave={handleSave}
    />
  ) : (
    <OtherSectionView company={company} onEditClick={handleEditClick} />
  );
}

type OtherSectionViewProps = {
  company: CompanyPageCompanyFragment;
  onEditClick: () => void;
};

function OtherSectionView({ company, onEditClick }: OtherSectionViewProps) {
  return (
    <div className={styles.viewContainer}>
      <div className={styles.header}>
        <h3 className={styles.title}>Other</h3>
        <Button variant="secondary" size="medium" onClick={onEditClick}>
          Edit
        </Button>
      </div>
      <div className={styles.content}>
        <InfoItem
          label="Display legal acknowledgements in SZ 2.0"
          value={
            company.isLegalAcknowledgementsEnabled ? 'Enabled' : 'Disabled'
          }
        />
        <InfoItem
          label={ALTERNATIVE_SUPPORT_EMAIL_LABEL}
          value={
            company.isAlternativeSupportEmailEnabled ? 'Enabled' : 'Disabled'
          }
        />
        {company.isAlternativeSupportEmailEnabled && (
          <InfoItem
            label={ALTERNATIVE_SUPPORT_EMAIL_ADDRESS_LABEL}
            value={company.alternativeSupportEmail ?? ''}
            hasCopyButton
          />
        )}
        <InfoItem
          label={EXPLICIT_CONSENT_EMAIL_LABEL}
          value={company.explicitConsentEmail || EXPLICIT_CONSENT_EMAIL_NOT_SET}
          hasCopyButton={Boolean(company.explicitConsentEmail)}
        />
        <InfoItem
          label="Display live chat to users"
          value={company.isLiveChatEnabled ? 'Enabled' : 'Disabled'}
        />
      </div>
    </div>
  );
}

type OtherSectionFormProps = {
  company: CompanyPageCompanyFragment;
  onCancelClick: () => void;
  onSave: () => void;
};

type NotificationVisibility = Partial<
  Record<
    keyof Pick<
      OtherSectionFormValues,
      | 'isLegalAcknowledgementsEnabled'
      | 'isAlternativeSupportEmailEnabled'
      | 'isLiveChatEnabled'
    >,
    boolean
  >
>;

const dataTestId = 'other-section-form';

function OtherSectionForm({
  company,
  onCancelClick,
  onSave,
}: OtherSectionFormProps) {
  const { handleMsgType } = useNotification();

  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();
  const [notificationVisibility, setNotificationVisibility] =
    useState<NotificationVisibility>({});

  const handleSave = (
    values: OtherSectionFormValues,
    { setFieldError }: FormikHelpers<OtherSectionFormValues>
  ) => {
    const isAlternativeSupportEmailEnabled =
      values.isAlternativeSupportEmailEnabled === EnableDisableValue.Enabled;

    editCompanyMutation({
      variables: {
        id: company.id,
        name: company.name,
        isLegalAcknowledgementsEnabled:
          values.isLegalAcknowledgementsEnabled === EnableDisableValue.Enabled,
        isAlternativeSupportEmailEnabled,
        // The address is only sent while the setting is on. Off, the stored
        // address is left alone (and hidden), so re-enabling restores it.
        ...(isAlternativeSupportEmailEnabled && {
          alternativeSupportEmail: values.alternativeSupportEmail.trim(),
        }),
        // Empty clears the column; declines then fall back to the support address.
        explicitConsentEmail: values.explicitConsentEmail.trim() || null,
        isLiveChatEnabled:
          values.isLiveChatEnabled === EnableDisableValue.Enabled,
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Other settings updated.',
          dismissable: true,
        });
        onSave();
      },
      onError: (error: ApolloError) => {
        // The monolith rejects enabling without a valid address with a
        // BadRequest; show its message under the input and in the toast.
        if (isAlternativeSupportEmailEnabled && error.message) {
          setFieldError('alternativeSupportEmail', error.message);
        }
        handleMsgType({
          type: TNotification.error,
          title: 'Other settings could not be updated',
          message: error.message || 'Something went wrong. Please try again.',
          dismissable: true,
        });
      },
    });
  };

  const handleChangeRadioButtonGroup = (_value: string, id: string) => {
    setNotificationVisibility(prev => ({
      ...prev,
      [id]: true,
    }));
  };

  const initialValues: OtherSectionFormValues = {
    isLegalAcknowledgementsEnabled: company.isLegalAcknowledgementsEnabled
      ? EnableDisableValue.Enabled
      : EnableDisableValue.Disabled,
    isAlternativeSupportEmailEnabled: company.isAlternativeSupportEmailEnabled
      ? EnableDisableValue.Enabled
      : EnableDisableValue.Disabled,
    alternativeSupportEmail: company.alternativeSupportEmail ?? '',
    explicitConsentEmail: company.explicitConsentEmail ?? '',
    isLiveChatEnabled: company.isLiveChatEnabled
      ? EnableDisableValue.Enabled
      : EnableDisableValue.Disabled,
  };

  return (
    <Formik<OtherSectionFormValues>
      initialValues={initialValues}
      validationSchema={otherSectionValidationSchema}
      onSubmit={handleSave}
    >
      {({ submitForm, values }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title="Edit Other"
          formId="other-section-form"
          hasResetAllButton={false}
          data-testid={dataTestId}
        >
          <form
            onSubmit={e => e.preventDefault()}
            id="other-section-form-element"
            className={styles.form}
          >
            <div>
              <FormikRadioButtonGroup
                id="isLegalAcknowledgementsEnabled"
                name="isLegalAcknowledgementsEnabled"
                options={enableDisableRadioButtonOptions}
                layout="horizontal"
                label="Display legal acknowledgements in SZ 2.0"
                data-testid={`${dataTestId}__legal-acknowledgements-radio-group`}
                useFormikField={useField}
                className={styles.radioGroup}
                onChange={handleChangeRadioButtonGroup}
              />
              {notificationVisibility.isLegalAcknowledgementsEnabled && (
                <InlineNotification
                  notificationType={NotificationType.NEUTRAL}
                  className={styles.inlineNotification}
                >
                  {values.isLegalAcknowledgementsEnabled ===
                  EnableDisableValue.Enabled
                    ? LEGAL_ACKNOWLEDGEMENTS_ENABLING_DISCLAIMER
                    : LEGAL_ACKNOWLEDGEMENTS_DISABLING_DISCLAIMER}
                </InlineNotification>
              )}
            </div>
            <div>
              <FormikRadioButtonGroup
                id="isAlternativeSupportEmailEnabled"
                name="isAlternativeSupportEmailEnabled"
                options={enableDisableRadioButtonOptions}
                layout="horizontal"
                label={ALTERNATIVE_SUPPORT_EMAIL_LABEL}
                data-testid={`${dataTestId}__alternative-support-email-radio-group`}
                useFormikField={useField}
                className={styles.radioGroup}
                onChange={handleChangeRadioButtonGroup}
              />
              {values.isAlternativeSupportEmailEnabled ===
                EnableDisableValue.Enabled && (
                <FormikTextInputField
                  id="alternativeSupportEmail"
                  type="email"
                  label={ALTERNATIVE_SUPPORT_EMAIL_ADDRESS_LABEL}
                  placeholder={ALTERNATIVE_SUPPORT_EMAIL_ADDRESS_LABEL}
                  useFormikField={useField}
                  className={styles.nestedField}
                  data-testid={`${dataTestId}__alternative-support-email-input`}
                />
              )}
              {notificationVisibility.isAlternativeSupportEmailEnabled && (
                <InlineNotification
                  notificationType={NotificationType.NEUTRAL}
                  className={styles.inlineNotification}
                >
                  {values.isAlternativeSupportEmailEnabled ===
                  EnableDisableValue.Enabled
                    ? ALTERNATIVE_SUPPORT_EMAIL_ENABLING_DISCLAIMER
                    : ALTERNATIVE_SUPPORT_EMAIL_DISABLING_DISCLAIMER}
                </InlineNotification>
              )}
            </div>
            <div>
              <FormikTextInputField
                id="explicitConsentEmail"
                type="email"
                label={EXPLICIT_CONSENT_EMAIL_LABEL}
                placeholder={EXPLICIT_CONSENT_EMAIL_LABEL}
                useFormikField={useField}
                className={styles.emailField}
                data-testid={`${dataTestId}__explicit-consent-email-input`}
              />
              <p className={styles.fieldHint}>{EXPLICIT_CONSENT_EMAIL_HINT}</p>
            </div>
            <div>
              <FormikRadioButtonGroup
                id="isLiveChatEnabled"
                name="isLiveChatEnabled"
                options={enableDisableRadioButtonOptions}
                layout="horizontal"
                label="Display live chat to users"
                data-testid={`${dataTestId}__live-chat-radio-group`}
                useFormikField={useField}
                className={styles.radioGroup}
                onChange={handleChangeRadioButtonGroup}
              />
              {notificationVisibility.isLiveChatEnabled && (
                <InlineNotification
                  notificationType={NotificationType.NEUTRAL}
                  className={styles.inlineNotification}
                >
                  {values.isLiveChatEnabled === EnableDisableValue.Enabled
                    ? LIVE_CHAT_ENABLING_DISCLAIMER
                    : LIVE_CHAT_DISABLING_DISCLAIMER}
                </InlineNotification>
              )}
            </div>
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
}

export default OtherSection;
